import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Alert, AlertTitle, Snackbar, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatTeamTaskFeedback } from "../utils/teamTaskUserFeedback";

const TeamTasksFeedbackContext = createContext(null);

export function TeamTasksFeedbackProvider({ children }) {
  const { t } = useTranslation("teamTasks");
  const [feedback, setFeedback] = useState(null);

  const clearFeedback = useCallback(() => setFeedback(null), []);

  const showError = useCallback(
    (err, context = "generic") => {
      const formatted = formatTeamTaskFeedback(err, context, t);
      setFeedback({ severity: "error", ...formatted });
    },
    [t]
  );

  const showSuccess = useCallback((message) => {
    setFeedback({ severity: "success", message, hint: null, title: null });
  }, []);

  const value = useMemo(
    () => ({ showError, showSuccess, clearFeedback }),
    [showError, showSuccess, clearFeedback]
  );

  return (
    <TeamTasksFeedbackContext.Provider value={value}>
      {children}
      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={feedback?.severity === "success" ? 3500 : 9000}
        onClose={clearFeedback}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={feedback?.severity || "error"}
          variant="filled"
          onClose={clearFeedback}
          sx={{ width: "100%", maxWidth: { xs: "calc(100vw - 32px)", sm: 440 } }}
        >
          {feedback?.severity === "error" && feedback?.title ? (
            <AlertTitle sx={{ fontWeight: 700 }}>{feedback.title}</AlertTitle>
          ) : null}
          {feedback?.message}
          {feedback?.hint ? (
            <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.92, lineHeight: 1.5 }}>
              {feedback.hint}
            </Typography>
          ) : null}
        </Alert>
      </Snackbar>
    </TeamTasksFeedbackContext.Provider>
  );
}

export function useTeamTasksFeedback() {
  const ctx = useContext(TeamTasksFeedbackContext);
  if (!ctx) {
    throw new Error("useTeamTasksFeedback must be used within TeamTasksFeedbackProvider");
  }
  return ctx;
}
